import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CONSTANTS } from "@constants/index";
import { useAppMode } from "@contexts/app-mode";
import { useLocationContext } from "@contexts/location/LocationContext";
import {
  ApiPaymentMethod,
  ApiPaymentMethodsResponse,
  ApiSavedCardsResponse,
  ClubData,
  LocationData,
  PaymentMethodItem,
  SavedPaymentMethod,
  UsePaymentMethodParams,
  UsePaymentMethodReturn,
} from "@models/components/paymentMethod.model";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import { RootState } from "@store/index";
import { useQuery } from "@tanstack/react-query";
import { toastService } from "@utils/common.utils";

export const paymentMethodKeys = {
  list: (params?: UsePaymentMethodParams) =>
    ["paymentMethods", "list", params] as const,
  all: ["paymentMethods"] as const,
};

export const usePaymentMethod = (
  params?: UsePaymentMethodParams
): UsePaymentMethodReturn => {
  const { t } = useTranslation();
  const { isKioskMode: isContextKioskMode } = useAppMode();
  const isKioskMode = params?.isKioskMode ?? isContextKioskMode;
  const isBooking =
    params?.isBooking ??
    Boolean(
      params?.enabled !== undefined ||
        params?.locationId ||
        params?.resourceId ||
        params?.date
    );

  const [clubs, setClubs] = useState<ClubData[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [removalError, setRemovalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // States for deleting/removing a payment card
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
  const [methodToRemove, setMethodToRemove] = useState<{
    methodId: string;
  } | null>(null);

  const hasFetchedRef = useRef<boolean>(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  // Fetch tokenisation payment methods
  const fetchPaymentMethods = useCallback(async () => {
    if (isBooking || isKioskMode) return;
    if (fetchPromiseRef.current) return fetchPromiseRef.current;

    setIsLoading(true);
    setRemovalError(null);

    const promise = (async () => {
      try {
        const res = await API_SERVICES.USER.GET_TOKENISATION_PAYMENT_METHODS();
        const responseData = handleResponse(res);
        if (responseData?.ok && responseData?.data) {
          const dataObj: ApiPaymentMethodsResponse = responseData.data;
          const mappedClubs: ClubData[] = Object.keys(dataObj).map(clubId => {
            const club = dataObj[clubId];
            const clubName =
              applyLocalization({
                default: club.info.default,
                locales: club.info.locales,
              }) ||
              club.info.default ||
              "";
            const apiLocations = club.locations || {};
            const locations: LocationData[] = Object.keys(apiLocations).map(
              locId => {
                const loc = apiLocations[locId];
                const locName =
                  applyLocalization({
                    default: loc.info.title.default,
                    locales: loc.info.title.locales || {},
                  }) ||
                  loc.info.title.default ||
                  "";
                const methods: PaymentMethodItem[] = (loc.paymentMethod || [])
                  .map((method): PaymentMethodItem => {
                    const isTwintMethod =
                      method.cardType?.toLowerCase() === "twint" ||
                      method.brand?.toLowerCase().includes("twint");
                    const rawNumber = String(method.number || "");
                    const lastFour = rawNumber ? rawNumber.slice(-4) : "";
                    const rawBrand =
                      method?.brand ||
                      (isTwintMethod
                        ? CONSTANTS.CARD.TWINT
                        : CONSTANTS.CARD.DEFAULT_BRAND || "Card");
                    const formattedBrand =
                      rawBrand.charAt(0).toUpperCase() +
                      rawBrand.slice(1).toLowerCase();

                    const expMonthValue = String(method.expMonth ?? "").trim();
                    const expYearValue = String(method.expYear ?? "").trim();

                    const expMonth =
                      expMonthValue &&
                      expMonthValue !== "undefined" &&
                      expMonthValue !== "null"
                        ? expMonthValue
                        : undefined;

                    const expYear =
                      expYearValue &&
                      expYearValue !== "undefined" &&
                      expYearValue !== "null"
                        ? expYearValue
                        : undefined;

                    return {
                      brand: method.brand
                        ? method.brand.toUpperCase()
                        : isTwintMethod
                          ? CONSTANTS.CARD.TWINT
                          : CONSTANTS.CARD.DEFAULT_BRAND,
                      details:
                        isTwintMethod || !expMonth || !expYear
                          ? ""
                          : `${expMonth}/${expYear}`,
                      expMonth,
                      expYear,
                      id: String(method.uuid || method.id || ""),
                      isBlocked: Boolean(method.isBlocked),
                      isDefault: method.isDefault,
                      isHidden: isTwintMethod ? false : true,
                      label: isTwintMethod
                        ? CONSTANTS.CARD.TWINT
                        : `${formattedBrand} •••• ${lastFour}`,
                      lastFour: isTwintMethod ? "" : lastFour,
                      type: isTwintMethod ? "twint" : "card",
                    };
                  })
                  .sort(
                    (a, b) =>
                      Number(Boolean(b.isDefault)) -
                      Number(Boolean(a.isDefault))
                  );

                return {
                  id: locId,
                  methods,
                  name: locName,
                };
              }
            );

            return {
              id: clubId,
              locations,
              name: clubName,
            };
          });

          setClubs(mappedClubs);

          // Pre-select Club and Location (preserve current selections if they still exist)
          if (mappedClubs.length > 0) {
            setSelectedClubId(prevClubId => {
              const currentSelectedClub =
                mappedClubs.find(c => c.id === prevClubId) || mappedClubs[0];
              setSelectedLocationId(prevLocId => {
                const locExists = currentSelectedClub.locations.some(
                  l => l.id === prevLocId
                );
                return locExists
                  ? prevLocId
                  : currentSelectedClub.locations[0]?.id || "";
              });
              return currentSelectedClub.id;
            });
          }
        }
      } catch (err) {
        console.error("Error fetching payment methods:", err);
      } finally {
        setIsLoading(false);
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = promise;
    return promise;
  }, [isBooking, isKioskMode, t]);

  useEffect(() => {
    if (!isBooking && !isKioskMode && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPaymentMethods();
    }
  }, [fetchPaymentMethods, isBooking, isKioskMode]);

  // Handle switching clubs
  const handleSelectClub = (clubId: string) => {
    setSelectedClubId(clubId);
    setRemovalError(null);
    const club = clubs.find(c => c.id === clubId);
    if (club && club.locations.length > 0) {
      setSelectedLocationId(club.locations[0].id);
    } else {
      setSelectedLocationId("");
    }
  };

  useEffect(() => {
    setRemovalError(null);
  }, [selectedLocationId]);

  // Toggle card details hidden/shown
  const handleToggleHide = useCallback(
    (clubId: string, locationId: string, methodId: string) => {
      setClubs(prev =>
        prev.map(club => {
          if (club.id !== clubId) return club;
          return {
            ...club,
            locations: club.locations.map(loc => {
              if (loc.id !== locationId) return loc;
              return {
                ...loc,
                methods: loc.methods.map(method => {
                  if (method.id !== methodId) return method;
                  return {
                    ...method,
                    isHidden: !method.isHidden,
                  };
                }),
              };
            }),
          };
        })
      );
    },
    []
  );

  // Make a card default for the current location
  const handleMakeDefault = useCallback(
    async (methodId: string) => {
      setIsLoading(true);
      try {
        const res =
          await API_SERVICES.USER.MAKE_DEFAULT_PAYMENT_METHOD(methodId);
        const responseData = handleResponse(res);
        const isSuccess =
          Boolean(responseData?.ok) && responseData?.data?.status !== false;

        if (isSuccess) {
          // Optimistically update local state immediately
          setClubs(prev =>
            prev.map(club => ({
              ...club,
              locations: club.locations.map(loc => {
                const hasMethod = loc.methods.some(m => m.id === methodId);
                if (!hasMethod) return loc;
                return {
                  ...loc,
                  methods: loc.methods
                    .map(m => ({
                      ...m,
                      isDefault: m.id === methodId,
                    }))
                    .sort(
                      (a, b) =>
                        Number(Boolean(b.isDefault)) -
                        Number(Boolean(a.isDefault))
                    ),
                };
              }),
            }))
          );
          toastService(t("paymentMethods.updateSuccess"));
          await fetchPaymentMethods();
        } else {
          const rawMessage =
            responseData?.data?.message ||
            responseData?.message ||
            res?.data?.message;
          if (rawMessage) {
            toastService(t(rawMessage, { defaultValue: rawMessage }));
          }
        }
      } catch (err) {
        console.error("Error setting default payment method:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPaymentMethods, t]
  );

  // Request removing a payment card
  const handleRequestRemove = useCallback((methodId: string) => {
    setMethodToRemove({ methodId });
    setIsRemoveModalOpen(true);
  }, []);

  // Confirm removing a payment card
  const handleConfirmRemove = useCallback(async () => {
    if (!methodToRemove) return;
    const { methodId } = methodToRemove;

    setIsLoading(true);
    setRemovalError(null);
    try {
      const res = await API_SERVICES.USER.DELETE_PAYMENT_METHOD(methodId);
      const responseData = handleResponse(res, undefined, false);
      const isSuccess =
        Boolean(responseData?.ok) && responseData?.data?.status !== false;

      if (isSuccess) {
        // Optimistically remove from state immediately
        setClubs(prev =>
          prev.map(club => ({
            ...club,
            locations: club.locations.map(loc => ({
              ...loc,
              methods: loc.methods.filter(m => m.id !== methodId),
            })),
          }))
        );
        setIsRemoveModalOpen(false);
        setMethodToRemove(null);
        toastService(
          t("paymentMethods.removeSuccess", {
            defaultValue: "Card removed successfully",
          })
        );
        await fetchPaymentMethods();
      } else {
        const rawMessage =
          responseData?.data?.message ||
          responseData?.message ||
          res?.data?.message ||
          res?.message;

        const rawCount =
          responseData?.data?.scheduledBookingsCount ??
          responseData?.scheduledBookingsCount ??
          res?.data?.scheduledBookingsCount;

        const parsedCount = Number(rawCount);
        const scheduledBookingsCount =
          rawCount !== null &&
          rawCount !== undefined &&
          !Number.isNaN(parsedCount)
            ? parsedCount
            : 1;

        let errorMsg = t("paymentMethods.removalRestricted", {
          count: scheduledBookingsCount,
          defaultValue: `Payment method can't be removed right now because it is associated with ${scheduledBookingsCount} scheduled booking${scheduledBookingsCount > 1 ? "s" : ""} currently being processed. Please try again after the payment${scheduledBookingsCount > 1 ? "s are" : " is"} settled.`,
        });

        if (rawMessage === "payment_method_in_use") {
          errorMsg = t("paymentMethods.removalRestricted", {
            count: scheduledBookingsCount,
            defaultValue: `Payment method can't be removed right now because it is associated with ${scheduledBookingsCount} scheduled booking${scheduledBookingsCount > 1 ? "s" : ""} currently being processed. Please try again after the payment${scheduledBookingsCount > 1 ? "s are" : " is"} settled.`,
          });
        } else if (rawMessage && t(rawMessage) !== rawMessage) {
          errorMsg = t(rawMessage, { count: scheduledBookingsCount });
        } else if (rawMessage) {
          errorMsg = rawMessage;
        }

        setRemovalError(errorMsg);
        setIsRemoveModalOpen(false);
        setMethodToRemove(null);
      }
    } catch (err) {
      console.error("Error deleting payment method:", err);
      setRemovalError(
        t("paymentMethods.removalRestricted", {
          count: 1,
          defaultValue:
            "Payment method can't be removed right now because it is associated with 1 scheduled booking currently being processed. Please try again after the payment is settled.",
        })
      );
      setIsRemoveModalOpen(false);
      setMethodToRemove(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchPaymentMethods, methodToRemove, t]);

  const currentClub = clubs.find(c => c.id === selectedClubId);
  const currentLoc = currentClub?.locations.find(
    l => l.id === selectedLocationId
  );

  const { activeLocation } = useLocationContext();
  const { bookingEditData, selectedDate, selectedSlot, selectedTime } =
    useSelector((state: RootState) => state.booking);

  const locationId =
    params?.locationId ||
    activeLocation?.id ||
    bookingEditData?.location?.id ||
    "";

  const resourceId =
    params?.resourceId ||
    selectedSlot?.resourceId ||
    bookingEditData?.resource?.id ||
    "";

  const date =
    params?.date ||
    selectedDate ||
    bookingEditData?.startDate ||
    moment().format("YYYY-MM-DD");

  const startTime =
    params?.startTime ||
    selectedTime ||
    bookingEditData?.startTime ||
    selectedSlot?.slot?.starts ||
    "";

  const endTime =
    params?.endTime ||
    (selectedSlot?.slot?.ends
      ? selectedSlot.slot.ends
      : selectedTime && selectedSlot?.slot?.slot
        ? moment(selectedTime, "HH:mm")
            .add(Number(selectedSlot.slot.slot), "minutes")
            .format("HH:mm")
        : bookingEditData?.endTime || "");

  const queryPayload = useMemo(
    () =>
      !isKioskMode && isBooking && locationId && resourceId && date
        ? {
            date: String(date),
            locationId: String(locationId),
            resourceId: String(resourceId),
            ...(startTime ? { startTime: String(startTime) } : {}),
            ...(endTime ? { endTime: String(endTime) } : {}),
          }
        : undefined,
    [date, endTime, isBooking, isKioskMode, locationId, resourceId, startTime]
  );

  const isEnabled = params?.enabled ?? true;

  const {
    data,
    isLoading: isSavedCardsLoading,
    refetch,
  } = useQuery<ApiSavedCardsResponse | null>({
    queryFn: async () => {
      if (
        isKioskMode ||
        !queryPayload ||
        !queryPayload?.startTime ||
        !queryPayload?.endTime
      )
        return null;
      const res =
        await API_SERVICES.USER.GET_TOKENISATION_SAVE_CARDS(queryPayload);
      const responseData = handleResponse(res);
      if (responseData?.ok && responseData?.data) {
        return responseData.data;
      }
      return null;
    },
    enabled: Boolean(
      !isKioskMode && isBooking && isEnabled && locationId && resourceId && date
    ),
    queryKey: paymentMethodKeys.list(queryPayload),
    staleTime: CONSTANTS.CACHE_TIME.THIRTY_SECONDS,
  });

  const mapPaymentMethodItem = useCallback(
    (pm: ApiPaymentMethod): SavedPaymentMethod => {
      const isTwintMethod =
        pm.cardType?.toLowerCase() === CONSTANTS.CARD.TWINT_LOWER ||
        pm.brand?.toLowerCase().includes("twint");
      const rawNumber = String(pm.number || "");
      const lastFour =
        rawNumber.length >= CONSTANTS.CARD.LAST_FOUR_DIGITS_LENGTH
          ? rawNumber.slice(-CONSTANTS.CARD.LAST_FOUR_DIGITS_LENGTH)
          : rawNumber;
      const brandStr =
        pm.brand ||
        (isTwintMethod ? CONSTANTS.CARD.TWINT : CONSTANTS.CARD.DEFAULT_BRAND);

      const expMonth =
        pm.expMonth != null &&
        String(pm.expMonth) !== "undefined" &&
        String(pm.expMonth) !== "null" &&
        String(pm.expMonth).trim() !== ""
          ? String(pm.expMonth)
          : undefined;
      const expYear =
        pm.expYear != null &&
        String(pm.expYear) !== "undefined" &&
        String(pm.expYear) !== "null" &&
        String(pm.expYear).trim() !== ""
          ? String(pm.expYear)
          : undefined;

      return {
        brand: brandStr.toUpperCase(),
        details:
          isTwintMethod || !expMonth || !expYear
            ? ""
            : `${expMonth}/${expYear}`,
        expMonth,
        expYear,
        id: String(pm.id !== undefined ? pm.id : pm.uuid || ""),
        isDefault: Boolean(pm.isDefault),
        lastFour: isTwintMethod ? "" : lastFour,
        type: isTwintMethod ? CONSTANTS.CARD.TWINT_LOWER : CONSTANTS.CARD.CARD,
      };
    },
    [t]
  );

  const { cancellationDate, savedMethods, scheduledTime } = useMemo(() => {
    if (!data) {
      return {
        cancellationDate: undefined,
        savedMethods: [],
        scheduledTime: undefined,
      };
    }
    const cardsList: ApiPaymentMethod[] = Array.isArray(data.savedCard)
      ? data.savedCard
      : data.savedCard && typeof data.savedCard === "object"
        ? Object.values(data.savedCard)
        : [];
    const methodsList = cardsList
      .filter(pm => !pm.isBlocked)
      .map(mapPaymentMethodItem)
      .sort(
        (a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault))
      );
    return {
      cancellationDate: data.cancellationDate,
      savedMethods: methodsList,
      scheduledTime: data.scheduledTime,
    };
  }, [data, mapPaymentMethodItem]);

  const [selectedMethodId, setSelectedMethodId] = useState<string>("new");

  // Preselect default saved method if available, otherwise preselect "new" method
  useEffect(() => {
    const defaultMethod = savedMethods.find(m => m.isDefault);
    if (defaultMethod) {
      setSelectedMethodId(defaultMethod.id);
    } else {
      setSelectedMethodId("new");
    }
  }, [savedMethods]);

  const savePaymentMethods = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    cancellationDate,
    clubs,
    currentClub,
    currentLoc,
    fetchPaymentMethods,
    handleConfirmRemove,
    handleMakeDefault,
    handleRequestRemove,
    handleSelectClub,
    handleToggleHide,
    isLoading: isLoading || isSavedCardsLoading,
    isRemoveModalOpen,
    methodToRemove,
    removalError,
    savedMethods,
    savePaymentMethods,
    scheduledTime,
    selectedClubId,
    selectedLocationId,
    selectedMethodId,
    setIsRemoveModalOpen,
    setSelectedLocationId,
    setSelectedMethodId,
  };
};
